import { useState, useEffect } from 'react'
import { 
  Users, Calendar, DollarSign, Package, X, Plus, Edit2, Trash2, 
  Save, AlertCircle, Check, Clock, Loader2,
  ShoppingCart, BarChart3, Settings, TrendingUp, Phone, RefreshCw,
  AlertTriangle
} from 'lucide-react'

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

// Stylist photos - hosted locally in public/stylists/
const STYLIST_PHOTOS: Record<string, string> = {
  'Dave Baker': '/stylists/dave.jpg',
  'Ben Hendry': '/stylists/ben.jpg',
  'Sylwia Donczyk': '/stylists/sylwia.jpg',
  'Callum Thayne': '/stylists/callum.jpg',
  'Elvis': '/stylists/elvis.jpg',
  'Joanna Eden': '/stylists/joanna.jpg',
  'Lily': '/stylists/lily.jpg',
}

// Types
interface Stylist {
  id: string
  name: string
  role: string
  bio: string | null
  years_experience: number
  specialties: string | null
  active: number
  color_hex: string
  display_order: number
}

interface Availability {
  id: string
  stylist_id: string
  day_of_week: number | null
  specific_date: string | null
  start_time: string | null
  end_time: string | null
  break_start: string | null
  break_end: string | null
  is_available: number
  notes: string | null
}

interface Service {
  id: string
  name: string
  duration: number
  default_price: number
  category: string
  active: number
}

interface StylistPricing {
  service_name: string
  duration: number
  default_price: number
  stylist_price: number | null
}

interface Product {
  id: string
  name: string
  sku: string | null
  category_id: string | null
  category_name?: string
  supplier_id: string | null
  supplier_name?: string
  cost_price: number
  retail_price: number
  current_stock: number
  min_stock_level: number
  unit: string
  size: string | null
  brand: string | null
  is_retail: number
  active: number
}

interface ProductCategory {
  id: string
  name: string
  description: string | null
}

interface DashboardData {
  generated_at: string
  today: {
    date: string
    appointments: number
    revenue: number
    cancelled: number
  }
  stylist_performance: Array<{
    id: string
    name: string
    color_hex: string
    appointment_count: number
    revenue: number
  }>
  cancellations: {
    today: number
    week: number
    month: number
  }
  low_stock: Array<{
    id: string
    name: string
    current_stock: number
    min_stock_level: number
    reorder_quantity: number
    unit: string
    brand: string
    supplier_name: string
  }>
  flagged_calls: Array<{
    id: string
    caller_phone: string
    transcript: string
    started_at: number
    customer_name: string
    snippet: string
  }>
  week_overview: Array<{
    date: string
    count: number
    revenue: number
    cancelled: number
  }>
  top_services: Array<{
    service: string
    count: number
    revenue: number
  }>
  upcoming_appointments: Array<{
    id: string
    time: string
    service: string
    duration: number
    price: number
    customer_name: string
    customer_phone: string
    stylist_name: string
    stylist_color: string
  }>
  recent_cancellations: Array<{
    id: string
    date: string
    time: string
    service: string
    price: number
    customer_name: string
    stylist_name: string
    stylist_color: string
    reason: string
  }>
}

type Tab = 'dashboard' | 'stylists' | 'schedules' | 'pricing' | 'services' | 'stock'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const COLORS = ['#8B5CF6', '#EC4899', '#3B82F6', '#10B981', '#F97316', '#F59E0B', '#06B6D4', '#EF4444', '#84CC16', '#6366F1']

export function AdminPanel({ onClose }: { onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard')
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Settings className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 overflow-x-auto">
          <TabButton 
            icon={<TrendingUp className="h-4 w-4" />} 
            label="Dashboard" 
            active={activeTab === 'dashboard'} 
            onClick={() => setActiveTab('dashboard')} 
          />
          <TabButton 
            icon={<Users className="h-4 w-4" />} 
            label="Stylists" 
            active={activeTab === 'stylists'} 
            onClick={() => setActiveTab('stylists')} 
          />
          <TabButton 
            icon={<Calendar className="h-4 w-4" />} 
            label="Schedules" 
            active={activeTab === 'schedules'} 
            onClick={() => setActiveTab('schedules')} 
          />
          <TabButton 
            icon={<DollarSign className="h-4 w-4" />} 
            label="Pricing" 
            active={activeTab === 'pricing'} 
            onClick={() => setActiveTab('pricing')} 
          />
          <TabButton 
            icon={<BarChart3 className="h-4 w-4" />} 
            label="Services" 
            active={activeTab === 'services'} 
            onClick={() => setActiveTab('services')} 
          />
          <TabButton 
            icon={<Package className="h-4 w-4" />} 
            label="Stock" 
            active={activeTab === 'stock'} 
            onClick={() => setActiveTab('stock')} 
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'stylists' && <StylistsTab />}
          {activeTab === 'schedules' && <SchedulesTab />}
          {activeTab === 'pricing' && <PricingTab />}
          {activeTab === 'services' && <ServicesTab />}
          {activeTab === 'stock' && <StockTab />}
        </div>
      </div>
    </div>
  )
}

function TabButton({ icon, label, active, onClick }: { 
  icon: React.ReactNode
  label: string
  active: boolean
  onClick: () => void 
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
        active 
          ? 'border-purple-600 text-purple-600' 
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

// ============================================
// DASHBOARD TAB - Business Intelligence
// ============================================
function DashboardTab() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)

  const fetchDashboard = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard`)
      const dashboardData = await res.json()
      setData(dashboardData)
      setLastRefresh(new Date())
    } catch (err) {
      console.error('Failed to fetch dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboard, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  if (loading && !data) return <LoadingState />

  if (!data) return (
    <div className="p-6 text-center text-gray-500">
      Failed to load dashboard data
    </div>
  )

  const hasAlerts = data.low_stock.length > 0 || data.flagged_calls.length > 0

  return (
    <div className="p-6 overflow-y-auto h-full space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Business Dashboard</h3>
          <p className="text-sm text-gray-500">
            {data.today.date} {lastRefresh && `• Updated ${lastRefresh.toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={fetchDashboard}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Alert Banner */}
      {hasAlerts && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Attention Required</p>
              <ul className="text-sm text-amber-700 mt-1 space-y-1">
                {data.low_stock.length > 0 && (
                  <li>• {data.low_stock.length} product(s) low on stock</li>
                )}
                {data.flagged_calls.length > 0 && (
                  <li>• {data.flagged_calls.length} call(s) flagged for review</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Today's Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard 
          label="Today's Bookings" 
          value={data.today.appointments} 
          icon={<Calendar className="h-5 w-5" />}
          color="blue"
        />
        <StatCard 
          label="Today's Revenue" 
          value={`£${data.today.revenue.toFixed(0)}`} 
          icon={<DollarSign className="h-5 w-5" />}
          color="green"
        />
        <StatCard 
          label="Cancellations (Today)" 
          value={data.cancellations.today} 
          icon={<X className="h-5 w-5" />}
          color={data.cancellations.today > 0 ? 'red' : 'gray'}
          subtext={`Week: ${data.cancellations.week} | Month: ${data.cancellations.month}`}
        />
        <StatCard 
          label="Low Stock Items" 
          value={data.low_stock.length} 
          icon={<Package className="h-5 w-5" />}
          color={data.low_stock.length > 0 ? 'amber' : 'gray'}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Stylist Performance */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">Stylist Performance Today</h4>
            {data.stylist_performance.length === 0 ? (
              <p className="text-sm text-gray-500">No appointments today</p>
            ) : (
              <div className="space-y-3">
                {data.stylist_performance.map((stylist) => (
                  <div key={stylist.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img 
                          src={STYLIST_PHOTOS[stylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=32`}
                          alt={stylist.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=32`
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{stylist.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{stylist.appointment_count} appts</p>
                      <p className="text-xs text-gray-500">£{(stylist.revenue || 0).toFixed(0)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Services */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">Top Services Today</h4>
            {data.top_services.length === 0 ? (
              <p className="text-sm text-gray-500">No services booked today</p>
            ) : (
              <div className="space-y-2">
                {data.top_services.map((service, i) => (
                  <div key={service.service} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                      <span className="text-sm text-gray-900">{service.service}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold">{service.count}x</span>
                      <span className="text-xs text-gray-500 ml-2">£{(service.revenue || 0).toFixed(0)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Appointments */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">Coming Up (Next 3 Hours)</h4>
            {data.upcoming_appointments.length === 0 ? (
              <p className="text-sm text-gray-500">No upcoming appointments</p>
            ) : (
              <div className="space-y-2">
                {data.upcoming_appointments.map((appt) => (
                  <div key={appt.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-2 h-8 rounded-full"
                        style={{ backgroundColor: appt.stylist_color || '#6B7280' }}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{appt.time} - {appt.service}</p>
                        <p className="text-xs text-gray-500">{appt.customer_name || 'Unknown'} • {appt.stylist_name}</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">£{appt.price || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Low Stock Alerts */}
          <div className={`border rounded-lg p-4 ${data.low_stock.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className={`h-4 w-4 ${data.low_stock.length > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              Stock Alerts
            </h4>
            {data.low_stock.length === 0 ? (
              <p className="text-sm text-gray-500">All stock levels OK</p>
            ) : (
              <div className="space-y-3">
                {data.low_stock.map((item) => (
                  <div key={item.id} className="bg-white p-3 rounded border border-red-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.brand} • {item.supplier_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-red-600">{item.current_stock} left</p>
                        <p className="text-xs text-gray-500">Min: {item.min_stock_level}</p>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                      Suggest reorder: {item.reorder_quantity} {item.unit}s
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Flagged Calls */}
          <div className={`border rounded-lg p-4 ${data.flagged_calls.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className={`h-4 w-4 ${data.flagged_calls.length > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
              Flagged Calls (Review Needed)
            </h4>
            {data.flagged_calls.length === 0 ? (
              <p className="text-sm text-gray-500">No flagged calls this week</p>
            ) : (
              <div className="space-y-3">
                {data.flagged_calls.map((call) => (
                  <div key={call.id} className="bg-white p-3 rounded border border-amber-200">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{call.customer_name || 'Unknown Caller'}</p>
                        <p className="text-xs text-gray-500">{call.caller_phone}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(call.started_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-700 bg-gray-50 p-2 rounded italic">
                      "{call.snippet}"
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Cancellations */}
          <div className={`border rounded-lg p-4 ${data.recent_cancellations?.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <X className={`h-4 w-4 ${data.recent_cancellations?.length > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              Recent Cancellations
            </h4>
            {!data.recent_cancellations || data.recent_cancellations.length === 0 ? (
              <p className="text-sm text-gray-500">No cancellations in the last 7 days</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {data.recent_cancellations.map((cancel) => (
                  <div key={cancel.id} className="bg-white p-3 rounded border border-red-200">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{cancel.customer_name}</p>
                        <p className="text-xs text-gray-500">{cancel.service} - £{cancel.price || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{cancel.date}</p>
                        <p className="text-xs text-gray-400">{cancel.time}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {cancel.stylist_name && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: cancel.stylist_color || '#6B7280' }}
                          />
                          {cancel.stylist_name}
                        </span>
                      )}
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                        {cancel.reason}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Week Overview */}
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-4">This Week</h4>
            {data.week_overview.length === 0 ? (
              <p className="text-sm text-gray-500">No data for this week</p>
            ) : (
              <div className="space-y-2">
                {data.week_overview.map((day) => {
                  const dayName = new Date(day.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' })
                  const isToday = day.date === data.today.date
                  return (
                    <div 
                      key={day.date} 
                      className={`flex items-center justify-between py-2 px-2 rounded ${isToday ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                          {dayName}
                        </span>
                        {isToday && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Today</span>}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-900">{day.count} appts</span>
                        <span className="text-green-600 font-medium">£{(day.revenue || 0).toFixed(0)}</span>
                        {day.cancelled > 0 && (
                          <span className="text-red-500 text-xs">-{day.cancelled}</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function StatCard({ 
  label, 
  value, 
  icon, 
  color,
  subtext 
}: { 
  label: string
  value: string | number
  icon: React.ReactNode
  color: 'blue' | 'green' | 'red' | 'amber' | 'gray'
  subtext?: string
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200'
  }

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide opacity-75">{label}</span>
        {icon}
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subtext && <p className="text-xs opacity-75 mt-1">{subtext}</p>}
    </div>
  )
}

// ============================================
// STYLISTS TAB
// ============================================
function StylistsTab() {
  const [stylists, setStylists] = useState<Stylist[]>([])
  const [loading, setLoading] = useState(true)
  const [editingStylist, setEditingStylist] = useState<Stylist | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    fetchStylists()
  }, [])

  const fetchStylists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stylists`)
      const data = await res.json()
      setStylists(data.stylists || [])
    } catch (err) {
      console.error('Failed to fetch stylists:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (stylist: Partial<Stylist>) => {
    try {
      const isNew = !stylist.id
      const url = isNew ? `${API_URL}/api/stylists` : `${API_URL}/api/stylists/${stylist.id}`
      const method = isNew ? 'POST' : 'PUT'
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stylist)
      })
      
      fetchStylists()
      setEditingStylist(null)
      setIsCreating(false)
    } catch (err) {
      console.error('Failed to save stylist:', err)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Deactivate this stylist?')) return
    try {
      await fetch(`${API_URL}/api/stylists/${id}`, { method: 'DELETE' })
      fetchStylists()
    } catch (err) {
      console.error('Failed to delete stylist:', err)
    }
  }

  if (loading) {
    return <LoadingState />
  }

  if (editingStylist || isCreating) {
    return (
      <StylistForm 
        stylist={editingStylist} 
        onSave={handleSave}
        onCancel={() => { setEditingStylist(null); setIsCreating(false) }}
      />
    )
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Team Members</h3>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Stylist
        </button>
      </div>

      <div className="grid gap-4">
        {stylists.map((stylist) => (
          <div 
            key={stylist.id}
            className={`flex items-center justify-between p-4 bg-white border rounded-lg ${
              stylist.active ? 'border-gray-200' : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src={STYLIST_PHOTOS[stylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=48`}
                  alt={stylist.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=48`
                  }}
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-900">{stylist.name}</h4>
                  {!stylist.active && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{stylist.role}</p>
                <p className="text-xs text-gray-400">{stylist.years_experience} years experience</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditingStylist(stylist)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Edit2 className="h-4 w-4 text-gray-500" />
              </button>
              <button
                onClick={() => handleDelete(stylist.id)}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StylistForm({ 
  stylist, 
  onSave, 
  onCancel 
}: { 
  stylist: Stylist | null
  onSave: (s: Partial<Stylist>) => void
  onCancel: () => void 
}) {
  const [form, setForm] = useState({
    name: stylist?.name || '',
    role: stylist?.role || 'Vault Stylist',
    bio: stylist?.bio || '',
    years_experience: stylist?.years_experience || 0,
    specialties: stylist?.specialties || '',
    color_hex: stylist?.color_hex || COLORS[0],
    active: stylist?.active ?? 1
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      ...stylist,
      ...form,
      id: stylist?.id
    })
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="max-w-2xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          {stylist ? 'Edit Stylist' : 'Add New Stylist'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="Vault Master">Vault Master (+25%)</option>
                <option value="Vault Stylist">Vault Stylist (Standard)</option>
                <option value="Junior Stylist">Junior Stylist (-20%)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Years Experience</label>
              <input
                type="number"
                value={form.years_experience}
                onChange={(e) => setForm({ ...form, years_experience: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calendar Color</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm({ ...form, color_hex: color })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      form.color_hex === color ? 'border-gray-900 scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Brief description of the stylist..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
            <input
              type="text"
              value={form.specialties}
              onChange={(e) => setForm({ ...form, specialties: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Balayage, Color Correction, Bridal"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="active"
              checked={form.active === 1}
              onChange={(e) => setForm({ ...form, active: e.target.checked ? 1 : 0 })}
              className="h-4 w-4 text-purple-600 rounded"
            />
            <label htmlFor="active" className="text-sm text-gray-700">Active (visible in calendar)</label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Save className="h-4 w-4" />
              Save Stylist
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================
// SCHEDULES TAB
// ============================================
function SchedulesTab() {
  const [stylists, setStylists] = useState<Stylist[]>([])
  const [selectedStylist, setSelectedStylist] = useState<string | null>(null)
  const [availability, setAvailability] = useState<Availability[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchStylists()
  }, [])

  useEffect(() => {
    if (selectedStylist) {
      fetchAvailability(selectedStylist)
    }
  }, [selectedStylist])

  const fetchStylists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stylists?active=1`)
      const data = await res.json()
      setStylists(data.stylists || [])
      if (data.stylists?.length > 0) {
        setSelectedStylist(data.stylists[0].id)
      }
    } catch (err) {
      console.error('Failed to fetch stylists:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailability = async (stylistId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/stylists/${stylistId}/availability`)
      const data = await res.json()
      setAvailability(data.availability || [])
    } catch (err) {
      console.error('Failed to fetch availability:', err)
    }
  }

  const handleSaveSchedule = async (dayOfWeek: number, schedule: Partial<Availability>) => {
    if (!selectedStylist) return
    setSaving(true)
    
    try {
      await fetch(`${API_URL}/api/stylists/${selectedStylist}/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          day_of_week: dayOfWeek,
          ...schedule
        })
      })
      fetchAvailability(selectedStylist)
    } catch (err) {
      console.error('Failed to save schedule:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />

  const currentStylist = stylists.find(s => s.id === selectedStylist)

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex gap-6">
        {/* Stylist Selector */}
        <div className="w-64 flex-shrink-0">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Select Stylist</h4>
          <div className="space-y-2">
            {stylists.map((stylist) => (
              <button
                key={stylist.id}
                onClick={() => setSelectedStylist(stylist.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  selectedStylist === stylist.id
                    ? 'bg-purple-50 border-2 border-purple-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
              >
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src={STYLIST_PHOTOS[stylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=32`}
                    alt={stylist.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=32`
                    }}
                  />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{stylist.name}</p>
                  <p className="text-xs text-gray-500">{stylist.role}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Schedule Grid */}
        <div className="flex-1">
          {currentStylist && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src={STYLIST_PHOTOS[currentStylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentStylist.name)}&background=${currentStylist.color_hex?.slice(1) || '888'}&color=fff&size=40`}
                    alt={currentStylist.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentStylist.name)}&background=${currentStylist.color_hex?.slice(1) || '888'}&color=fff&size=40`
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{currentStylist.name}</h3>
                  <p className="text-sm text-gray-500">Weekly Schedule</p>
                </div>
                {saving && <Loader2 className="h-4 w-4 animate-spin text-purple-600" />}
              </div>

              <div className="grid gap-3">
                {DAYS.map((day, index) => {
                  const schedule = availability.find(a => a.day_of_week === index && !a.specific_date)
                  return (
                    <DayScheduleRow 
                      key={day}
                      day={day}
                      dayIndex={index}
                      schedule={schedule}
                      onSave={(s) => handleSaveSchedule(index, s)}
                    />
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function DayScheduleRow({ 
  day, 
  dayIndex: _dayIndex, 
  schedule, 
  onSave 
}: { 
  day: string
  dayIndex: number
  schedule: Availability | undefined
  onSave: (s: Partial<Availability>) => void
}) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    is_available: schedule?.is_available ?? 0,
    start_time: schedule?.start_time || '09:00',
    end_time: schedule?.end_time || '18:00',
    break_start: schedule?.break_start || '',
    break_end: schedule?.break_end || ''
  })

  const handleSave = () => {
    onSave({
      is_available: form.is_available,
      start_time: form.is_available ? form.start_time : null,
      end_time: form.is_available ? form.end_time : null,
      break_start: form.break_start || null,
      break_end: form.break_end || null
    })
    setEditing(false)
  }

  const isOff = !form.is_available

  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${
      isOff ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'
    }`}>
      <div className="w-24">
        <span className={`font-medium ${isOff ? 'text-gray-400' : 'text-gray-900'}`}>{day}</span>
      </div>

      {editing ? (
        <div className="flex-1 flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_available === 1}
              onChange={(e) => setForm({ ...form, is_available: e.target.checked ? 1 : 0 })}
              className="h-4 w-4 text-purple-600 rounded"
            />
            <span className="text-sm">Working</span>
          </label>
          
          {form.is_available === 1 && (
            <>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="px-2 py-1 border rounded text-sm"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="px-2 py-1 border rounded text-sm"
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Break:</span>
                <input
                  type="time"
                  value={form.break_start}
                  onChange={(e) => setForm({ ...form, break_start: e.target.value })}
                  className="px-2 py-1 border rounded text-sm w-24"
                  placeholder="Start"
                />
                <span>-</span>
                <input
                  type="time"
                  value={form.break_end}
                  onChange={(e) => setForm({ ...form, break_end: e.target.value })}
                  className="px-2 py-1 border rounded text-sm w-24"
                  placeholder="End"
                />
              </div>
            </>
          )}
          
          <button
            onClick={handleSave}
            className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="p-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-between">
          {isOff ? (
            <span className="text-gray-400 text-sm">Day Off</span>
          ) : (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-700">
                <Clock className="h-4 w-4 inline mr-1" />
                {schedule?.start_time} - {schedule?.end_time}
              </span>
              {schedule?.break_start && (
                <span className="text-gray-500">
                  Break: {schedule.break_start} - {schedule.break_end}
                </span>
              )}
            </div>
          )}
          <button
            onClick={() => setEditing(true)}
            className="p-1.5 hover:bg-gray-100 rounded"
          >
            <Edit2 className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      )}
    </div>
  )
}

// ============================================
// PRICING TAB
// ============================================
function PricingTab() {
  const [stylists, setStylists] = useState<Stylist[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [selectedStylist, setSelectedStylist] = useState<string | null>(null)
  const [pricing, setPricing] = useState<StylistPricing[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (selectedStylist) {
      fetchPricing(selectedStylist)
    }
  }, [selectedStylist])

  const fetchData = async () => {
    try {
      const [stylistsRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/api/stylists?active=1`),
        fetch(`${API_URL}/api/services`)
      ])
      const [stylistsData, servicesData] = await Promise.all([
        stylistsRes.json(),
        servicesRes.json()
      ])
      setStylists(stylistsData.stylists || [])
      setServices(servicesData.services || [])
      if (stylistsData.stylists?.length > 0) {
        setSelectedStylist(stylistsData.stylists[0].id)
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchPricing = async (stylistId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/stylists/${stylistId}/pricing`)
      const data = await res.json()
      setPricing(data.pricing || [])
    } catch (err) {
      console.error('Failed to fetch pricing:', err)
    }
  }

  const handleSavePrice = async (serviceName: string, price: number | null) => {
    if (!selectedStylist) return
    
    try {
      await fetch(`${API_URL}/api/stylists/${selectedStylist}/pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceName: serviceName,
          price: price
        })
      })
      fetchPricing(selectedStylist)
    } catch (err) {
      console.error('Failed to save price:', err)
    }
  }

  if (loading) return <LoadingState />

  const currentStylist = stylists.find(s => s.id === selectedStylist)

  // Group services by category
  const servicesByCategory = services.reduce((acc, service) => {
    const cat = service.category || 'Other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(service)
    return acc
  }, {} as Record<string, Service[]>)

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex gap-6">
        {/* Stylist Selector */}
        <div className="w-64 flex-shrink-0">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Select Stylist</h4>
          <div className="space-y-2">
            {stylists.map((stylist) => (
              <button
                key={stylist.id}
                onClick={() => setSelectedStylist(stylist.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  selectedStylist === stylist.id
                    ? 'bg-purple-50 border-2 border-purple-500'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                }`}
              >
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src={STYLIST_PHOTOS[stylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=32`}
                    alt={stylist.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=32`
                    }}
                  />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{stylist.name}</p>
                  <p className="text-xs text-gray-500">{stylist.role}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Grid */}
        <div className="flex-1">
          {currentStylist && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src={STYLIST_PHOTOS[currentStylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentStylist.name)}&background=${currentStylist.color_hex?.slice(1) || '888'}&color=fff&size=40`}
                    alt={currentStylist.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(currentStylist.name)}&background=${currentStylist.color_hex?.slice(1) || '888'}&color=fff&size=40`
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{currentStylist.name}</h3>
                  <p className="text-sm text-gray-500">Price Overrides (blank = use default)</p>
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">{category}</h4>
                    <div className="grid gap-2">
                      {categoryServices.map((service) => {
                        const override = pricing.find(p => p.service_name === service.name)
                        return (
                          <PricingRow
                            key={service.id}
                            service={service}
                            override={override}
                            onSave={(price) => handleSavePrice(service.name, price)}
                          />
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function PricingRow({ 
  service, 
  override, 
  onSave 
}: { 
  service: Service
  override: StylistPricing | undefined
  onSave: (price: number | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [price, setPrice] = useState(override?.stylist_price?.toString() || '')

  const handleSave = () => {
    const numPrice = price ? parseFloat(price) : null
    onSave(numPrice)
    setEditing(false)
  }

  const hasOverride = override?.stylist_price !== null && override?.stylist_price !== undefined

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div>
        <span className="text-sm font-medium text-gray-900">{service.name}</span>
        <span className="text-xs text-gray-500 ml-2">({service.duration} min)</span>
      </div>
      
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">
          Default: £{service.default_price?.toFixed(2) || '0.00'}
        </span>
        
        {editing ? (
          <div className="flex items-center gap-2">
            <span className="text-gray-400">£</span>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-20 px-2 py-1 border rounded text-sm"
              placeholder="Override"
              step="0.01"
            />
            <button
              onClick={handleSave}
              className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setEditing(false); setPrice(override?.stylist_price?.toString() || '') }}
              className="p-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {hasOverride ? (
              <span className="text-sm font-medium text-purple-600">
                £{override!.stylist_price!.toFixed(2)}
              </span>
            ) : (
              <span className="text-sm text-gray-400">No override</span>
            )}
            <button
              onClick={() => setEditing(true)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <Edit2 className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// SERVICES TAB
// ============================================
function ServicesTab() {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    fetchServices()
  }, [])

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/services`)
      const data = await res.json()
      setServices(data.services || [])
    } catch (err) {
      console.error('Failed to fetch services:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (service: Partial<Service>) => {
    try {
      const isNew = !service.id
      const url = isNew ? `${API_URL}/api/services` : `${API_URL}/api/services/${service.id}`
      const method = isNew ? 'POST' : 'PUT'
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(service)
      })
      
      fetchServices()
      setEditingService(null)
      setIsCreating(false)
    } catch (err) {
      console.error('Failed to save service:', err)
    }
  }

  if (loading) return <LoadingState />

  if (editingService || isCreating) {
    return (
      <ServiceForm
        service={editingService}
        onSave={handleSave}
        onCancel={() => { setEditingService(null); setIsCreating(false) }}
      />
    )
  }

  // Group by category
  const servicesByCategory = services.reduce((acc, service) => {
    const cat = service.category || 'Other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(service)
    return acc
  }, {} as Record<string, Service[]>)

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Services & Pricing</h3>
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Service
        </button>
      </div>

      <div className="space-y-6">
        {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
          <div key={category}>
            <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">{category}</h4>
            <div className="grid gap-2">
              {categoryServices.map((service) => (
                <div 
                  key={service.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    service.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900">{service.name}</h5>
                      <p className="text-sm text-gray-500">{service.duration} minutes</p>
                    </div>
                    {!service.active && (
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inactive</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-semibold text-gray-900">
                      £{service.default_price?.toFixed(2) || '0.00'}
                    </span>
                    <button
                      onClick={() => setEditingService(service)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Edit2 className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ServiceForm({
  service,
  onSave,
  onCancel
}: {
  service: Service | null
  onSave: (s: Partial<Service>) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState({
    name: service?.name || '',
    duration: service?.duration || 30,
    default_price: service?.default_price || 0,
    category: service?.category || 'Ladies Cutting',
    active: service?.active ?? 1
  })

  const categories = [
    'Ladies Cutting', 'Mens', 'Childrens', 'Colour', 'Highlights', 
    'Styling', 'Treatment', 'Extensions', 'Spa'
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      ...service,
      ...form,
      id: service?.id
    })
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="max-w-xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          {service ? 'Edit Service' : 'Add New Service'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="5"
                step="5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Price (£)</label>
              <input
                type="number"
                value={form.default_price}
                onChange={(e) => setForm({ ...form, default_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="service-active"
              checked={form.active === 1}
              onChange={(e) => setForm({ ...form, active: e.target.checked ? 1 : 0 })}
              className="h-4 w-4 text-purple-600 rounded"
            />
            <label htmlFor="service-active" className="text-sm text-gray-700">Active (available for booking)</label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Save className="h-4 w-4" />
              Save Service
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================
// STOCK TAB
// ============================================
function StockTab() {
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<ProductCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'low' | 'retail'>('all')
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/products`),
        fetch(`${API_URL}/api/product-categories`)
      ])
      
      if (productsRes.ok) {
        const productsData = await productsRes.json()
        setProducts(productsData.products || [])
      }
      
      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json()
        setCategories(categoriesData.categories || [])
      }
    } catch (err) {
      console.error('Failed to fetch products:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (product: Partial<Product>) => {
    try {
      const isNew = !product.id
      const url = isNew ? `${API_URL}/api/products` : `${API_URL}/api/products/${product.id}`
      const method = isNew ? 'POST' : 'PUT'
      
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(product)
      })
      
      fetchData()
      setEditingProduct(null)
      setIsCreating(false)
    } catch (err) {
      console.error('Failed to save product:', err)
    }
  }

  const handleStockAdjust = async (productId: string, quantity: number, notes: string) => {
    try {
      await fetch(`${API_URL}/api/products/${productId}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity, notes })
      })
      fetchData()
    } catch (err) {
      console.error('Failed to adjust stock:', err)
    }
  }

  // Stock adjustment with "set to" option
  const [stockModal, setStockModal] = useState<{product: Product, mode: 'adjust' | 'set'} | null>(null)
  const [stockValue, setStockValue] = useState('')
  const [stockNotes, setStockNotes] = useState('')

  const handleStockSubmit = () => {
    if (!stockModal || !stockValue) return
    
    const value = parseInt(stockValue)
    if (isNaN(value)) return
    
    // If "set" mode, calculate the delta needed
    const quantity = stockModal.mode === 'set' 
      ? value - stockModal.product.current_stock 
      : value
    
    if (quantity === 0) {
      setStockModal(null)
      return
    }
    
    handleStockAdjust(stockModal.product.id, quantity, stockNotes || `Stock ${stockModal.mode === 'set' ? 'set to' : 'adjusted by'} ${value}`)
    setStockModal(null)
    setStockValue('')
    setStockNotes('')
  }

  if (loading) return <LoadingState />

  if (editingProduct || isCreating) {
    return (
      <ProductForm
        product={editingProduct}
        categories={categories}
        onSave={handleSave}
        onCancel={() => { setEditingProduct(null); setIsCreating(false) }}
      />
    )
  }

  const filteredProducts = products.filter(p => {
    if (filter === 'low') return p.current_stock < p.min_stock_level
    if (filter === 'retail') return p.is_retail === 1
    return true
  })

  const lowStockCount = products.filter(p => p.current_stock < p.min_stock_level).length

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Stock & Inventory</h3>
          {lowStockCount > 0 && (
            <span className="flex items-center gap-1 text-sm bg-red-100 text-red-700 px-3 py-1 rounded-full">
              <AlertCircle className="h-4 w-4" />
              {lowStockCount} low stock
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Products</option>
            <option value="low">Low Stock Only</option>
            <option value="retail">Retail Products</option>
          </select>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </button>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No products found</p>
          <p className="text-sm">Add products to start tracking inventory</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredProducts.map((product) => {
            const isLow = product.current_stock <= product.min_stock_level
            return (
              <div
                key={product.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  isLow ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{product.name}</h4>
                    {product.is_retail === 1 && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Retail</span>
                    )}
                    {isLow && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Low Stock</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {product.brand} {product.size && `- ${product.size}`}
                    {product.sku && <span className="ml-2 text-gray-400">SKU: {product.sku}</span>}
                  </p>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Stock</p>
                    <p className={`text-lg font-semibold ${isLow ? 'text-red-600' : 'text-gray-900'}`}>
                      {product.current_stock} <span className="text-sm font-normal text-gray-400">{product.unit}s</span>
                    </p>
                  </div>
                  
                  {product.is_retail === 1 && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Retail</p>
                      <p className="text-lg font-semibold text-gray-900">£{product.retail_price?.toFixed(2)}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setStockModal({ product, mode: 'set' })
                        setStockValue('')  // Start empty so user types new value
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Adjust Stock"
                    >
                      <ShoppingCart className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => setEditingProduct(product)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Edit2 className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {stockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Adjust Stock: {stockModal.product.name}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-2">
                Current stock: <span className="font-semibold text-gray-900">{stockModal.product.current_stock} {stockModal.product.unit}s</span>
              </p>
              <p className="text-sm text-gray-500">
                Min level: <span className="font-semibold">{stockModal.product.min_stock_level}</span>
              </p>
            </div>

            {/* Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => {
                  setStockModal({ ...stockModal, mode: 'set' })
                  setStockValue('')
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  stockModal.mode === 'set' 
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' 
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                }`}
              >
                Set To
              </button>
              <button
                onClick={() => {
                  setStockModal({ ...stockModal, mode: 'adjust' })
                  setStockValue('')
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  stockModal.mode === 'adjust' 
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' 
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                }`}
              >
                Adjust By
              </button>
            </div>

            {/* Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {stockModal.mode === 'set' ? 'New Stock Level' : 'Adjustment Amount'}
              </label>
              <input
                type="number"
                value={stockValue}
                onChange={(e) => setStockValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={stockModal.mode === 'set' ? 'Enter new stock level' : 'e.g., -5 to remove, +10 to add'}
              />
              {stockModal.mode === 'set' && stockValue && (
                <p className="text-xs text-gray-500 mt-1">
                  Will {parseInt(stockValue) > stockModal.product.current_stock ? 'add' : 'remove'} {Math.abs(parseInt(stockValue) - stockModal.product.current_stock)} {stockModal.product.unit}s
                </p>
              )}
              {stockModal.mode === 'adjust' && stockValue && (
                <p className="text-xs text-gray-500 mt-1">
                  New stock will be: {stockModal.product.current_stock + parseInt(stockValue || '0')} {stockModal.product.unit}s
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={stockNotes}
                onChange={(e) => setStockNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., Stocktake, Used for service"
              />
            </div>

            {/* Warning if going below min */}
            {stockModal.mode === 'set' && parseInt(stockValue) <= stockModal.product.min_stock_level && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  This will trigger a low stock alert
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStockModal(null)
                  setStockValue('')
                  setStockNotes('')
                }}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStockSubmit}
                disabled={
                  !stockValue || 
                  isNaN(parseInt(stockValue)) ||
                  (stockModal.mode === 'set' && parseInt(stockValue) === stockModal.product.current_stock) ||
                  (stockModal.mode === 'adjust' && parseInt(stockValue) === 0)
                }
                className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update Stock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ProductForm({
  product,
  categories: _categories,
  onSave,
  onCancel
}: {
  product: Product | null
  categories: ProductCategory[]
  onSave: (p: Partial<Product>) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState({
    name: product?.name || '',
    sku: product?.sku || '',
    category_id: product?.category_id || '',
    brand: product?.brand || '',
    size: product?.size || '',
    unit: product?.unit || 'unit',
    cost_price: product?.cost_price || 0,
    retail_price: product?.retail_price || 0,
    min_stock_level: product?.min_stock_level || 5,
    is_retail: product?.is_retail || 0,
    active: product?.active ?? 1
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      ...product,
      ...form,
      id: product?.id
    })
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="max-w-2xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          {product ? 'Edit Product' : 'Add New Product'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <input
                type="text"
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., INOA-6-60"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
              <input
                type="text"
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
              <input
                type="text"
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 250ml"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit Type</label>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="unit">Unit</option>
                <option value="tube">Tube</option>
                <option value="bottle">Bottle</option>
                <option value="box">Box</option>
                <option value="pack">Pack</option>
                <option value="tub">Tub</option>
                <option value="can">Can</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (£)</label>
              <input
                type="number"
                value={form.cost_price}
                onChange={(e) => setForm({ ...form, cost_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Retail Price (£)</label>
              <input
                type="number"
                value={form.retail_price}
                onChange={(e) => setForm({ ...form, retail_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
                {product?.current_stock ?? 0} {form.unit}s
                <span className="text-xs text-gray-400 ml-2">(Use stock adjustment icon to change)</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Stock Level (reorder alert)</label>
              <input
                type="number"
                value={form.min_stock_level}
                onChange={(e) => setForm({ ...form, min_stock_level: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="0"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_retail === 1}
                onChange={(e) => setForm({ ...form, is_retail: e.target.checked ? 1 : 0 })}
                className="h-4 w-4 text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">Available for retail sale</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.active === 1}
                onChange={(e) => setForm({ ...form, active: e.target.checked ? 1 : 0 })}
                className="h-4 w-4 text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Save className="h-4 w-4" />
              Save Product
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================
// SHARED COMPONENTS
// ============================================
function LoadingState() {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
    </div>
  )
}
